import Alpine from "alpinejs";
import EmblaCarousel from "embla-carousel";
import Fade from "embla-carousel-fade";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";
import { ScrollToPlugin } from 'gsap/all';

gsap.registerPlugin(
    ScrollTrigger, MotionPathPlugin, ScrollToPlugin
);

var sfpReport = {
    initScrollAnimations: function() {
        const AOS_SELECTOR = "[data-aos]";
        const STARTING_CLASS = "aos-starting-style";
        const OBSERVED_FLAG = "aosObserved";
        const DEFAULT_OFFSET_BOTTOM = "100px";
        const ENABLE_VIEWPORT_CHECK = true;

        

        const isFullyOutsideViewport = (element) => {
            const rect = element.getBoundingClientRect();
            return (
                rect.bottom <= 0 ||
                rect.top >= window.innerHeight ||
                rect.right <= 0 ||
                rect.left >= window.innerWidth
            );
        };

        const getOffsetBottom = (element) => {
            if (!element.hasAttribute("data-aos-offset")) {
                return DEFAULT_OFFSET_BOTTOM;
            }

            return element.getAttribute("data-aos-offset").trim();
        };

        const applyStartingStyle = (element) => {
            if (!ENABLE_VIEWPORT_CHECK || isFullyOutsideViewport(element)) {
                element.classList.add(STARTING_CLASS);
            } else {
                element.classList.remove(STARTING_CLASS);
            }
            
        };

        const callback = (entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.remove(STARTING_CLASS);
                    entry.target.classList.add("aos-animate");
                    observer.unobserve(entry.target);
                }
            });
        };

        if (typeof window.IntersectionObserver !== "function") {
            document.querySelectorAll(AOS_SELECTOR).forEach((element) => {
                element.classList.add("aos-animate");
            });
            return;
        }

        const observeElement = (element) => {
            if (element.dataset[OBSERVED_FLAG] === "true") {
                return;
            }

            applyStartingStyle(element);

            const observer = new IntersectionObserver(callback, {
                rootMargin: `0px 0px -${getOffsetBottom(element)} 0px`,
                threshold: 0,
            });

            observer.observe(element);
            element.dataset[OBSERVED_FLAG] = "true";
        };

        const setupObservers = () => {
            document.querySelectorAll(AOS_SELECTOR).forEach(observeElement);
        };

        if (document.readyState === "loading") {
            document.addEventListener("DOMContentLoaded", setupObservers, {
                once: true,
            });
        } else {
            setupObservers();
        }

    },
    throttle: function (callback, interval) {
        var lastCall = 0;

        return function () {
            var now = Date.now();

            if (now - lastCall >= interval) {
                lastCall = now;
                callback.apply(this, arguments);
            }
        
        }
    },
    initCarousel: function () {
        document.addEventListener("alpine:init", () => {
            Alpine.data("carousel", (count) => ({
                // Data
                emblaApi: null,
                carouselId: null,
                selectedIndex: 0,
                slideCount: count,
                canScrollPrev: false,
                canScrollNext: false,

                // Initialization
                init() {
                    this.carouselId = this.$id("carousel");

                    // Parse Embla options
                    const optionsAttr = this.$el.getAttribute("data-embla-options");
                    const options = optionsAttr ? JSON.parse(optionsAttr) : {};

                    // Initialize Embla with Fade plugin
                    this.emblaApi = EmblaCarousel(this.$refs.viewport, options, [Fade()]);

                    // Set up event listeners
                    this.emblaApi.on("select", () => this.updateState());
                    this.emblaApi.on("init", () => this.updateState());

                    // Initial state
                    this.updateState();
                },

                // State management
                updateState() {
                    if (!this.emblaApi) return;

                    this.selectedIndex = this.emblaApi.selectedScrollSnap();
                    this.canScrollPrev = this.emblaApi.canScrollPrev();
                    this.canScrollNext = this.emblaApi.canScrollNext();
                },

                // Navigation methods
                scrollPrev() {
                    this.emblaApi.scrollPrev();
                },

                scrollNext() {
                    this.emblaApi.scrollNext();
                },

                scrollTo(index) {
                    this.emblaApi.scrollTo(index);
                },

                // Keyboard navigation
                handleKeydown(event) {
                    const handlers = {
                        ArrowLeft: () => this.focusPrevTab(),
                        ArrowUp: () => this.focusPrevTab(),
                        ArrowRight: () => this.focusNextTab(),
                        ArrowDown: () => this.focusNextTab(),
                        Home: () => this.focusTab(0),
                        End: () => this.focusTab(this.slideCount - 1),
                    };

                    const handler = handlers[event.key];
                    if (handler) {
                        event.preventDefault();
                        handler();
                    }
                },

                focusPrevTab() {
                    const isLooping = this.emblaApi.internalEngine().options.loop;

                    if (this.selectedIndex === 0) {
                        if (isLooping) {
                            this.focusTab(this.slideCount - 1);
                        }
                    } else {
                        this.focusTab(this.selectedIndex - 1);
                    }
                },

                focusNextTab() {
                    const isLooping = this.emblaApi.internalEngine().options.loop;
                    const isLastSlide = this.selectedIndex === this.slideCount - 1;

                    if (isLastSlide) {
                        if (isLooping) {
                            this.focusTab(0);
                        }
                    } else {
                        this.focusTab(this.selectedIndex + 1);
                    }
                },

                focusTab(index) {
                    this.scrollTo(index);
                    this.$nextTick(() => {
                        const tab = this.$refs.tablist.querySelector(
                            `[role="tab"]:nth-child(${index + 1})`,
                        );
                        tab?.focus();
                    });
                },
            }));
        });
    },
    initScrollSpy() {
        const navs = document.querySelectorAll(".scroll-spy");

        if (!navs.length) {
            return;
        }

        navs.forEach((nav) => {
            const links = nav.querySelectorAll("a");

            links.forEach((link) => {
                link.section = document.getElementById(link.hash.substr(1));
            });

            function onScroll() {
                var activeLink = null;

                links.forEach((link) => {
                    if (link.section == null) return;

                    const rect = link.section.getBoundingClientRect();

                    if (rect.top <= window.innerHeight * 0.4) {
                        activeLink = link;
                    }
                });

                links.forEach((link) => {
                    if (activeLink == null || link != activeLink) {
                        link.classList.remove("active");
                    }
                });

                if (activeLink) {
                    activeLink.classList.add("active");
                }
            }

            window.addEventListener("scroll", sfpReport.throttle(onScroll, 50));
        });


    },
    initSectionHeaderBackgrounds() {
        const backgrounds = document.querySelectorAll(
            ".report-section-header__bg, .report-masthead__bg",
        );

        backgrounds.forEach((background) => {
            ScrollTrigger.create({
                trigger: background,
                start: "top 50%",
                once: true,
                onEnter: () => background.classList.add("is-active"),
            });
        });
    },
    initTimelineFish() {
        const timeline = document.querySelector(".report-timeline");
        const fish = document.querySelector("#fish");
        const items = timeline?.querySelectorAll(".report-timeline__item");
        const reportBlock = timeline?.closest(".report-timeline-block");
        const markers = reportBlock?.querySelectorAll(".timeline-marker");
        
        const backgrounds = reportBlock?.querySelectorAll(".report-timeline-background__item");

        const thresholdFromTop = "45%";
        const showMarkers = false;

        const segments = Array.from(
            reportBlock?.querySelectorAll("[id^='segment-']") || [],
        ).sort((first, second) => first.id.localeCompare(second.id, undefined, { numeric: true }));

        if (!timeline || !fish || !items?.length || !segments.length || items.length < segments.length) {
            return;
        }

        const setActiveBackground = (backgroundIndex) => {
            const activeIndex = Math.max(
                0,
                Math.min(backgroundIndex, (backgrounds?.length || 1) - 1),
            );

            Array.from(backgrounds || []).forEach((background, index) => {
                background.classList.toggle("is-active", index === activeIndex);
            });

            Array.from(markers || []).forEach((marker, index) => {
                marker.classList.toggle("is-active", index === activeIndex - 1);
            });

            Array.from(items).forEach((item, index) => {
                item.classList.toggle("is-active", index === activeIndex);
            });
        };

        setActiveBackground(0);

        let markerScrollTween = null;
        const root = document.documentElement;
        const nativeScrollBehavior = root.style.scrollBehavior;

        Array.from(markers || []).forEach((marker, markerIndex) => {
            const item = items[Math.min(markerIndex + 1, items.length - 1)];

            if (!marker || !item) {
                return;
            }

            marker.addEventListener("click", () => {
                setActiveBackground(markerIndex + 1);

                markerScrollTween?.kill();
                root.style.scrollBehavior = "auto";

                markerScrollTween = gsap.to(window, {
                    duration: 0.4,
                    // ease: "linear",
                    ease: "power2.out",
                    scrollTo: {
                        y: item,
                        offsetY: 90,
                    },
                    onComplete: () => {
                        root.style.scrollBehavior = nativeScrollBehavior;
                        markerScrollTween = null;
                    },
                    onInterrupt: () => {
                        root.style.scrollBehavior = nativeScrollBehavior;
                        markerScrollTween = null;
                    },
                });
            });
        });

        const segmentTweens = [];

        segments.slice(0, items.length).forEach((segment, itemIndex) => {
            const item = items[itemIndex];
            const nextItem = items[itemIndex + 1];
            const backgroundImage = backgrounds?.[itemIndex]?.querySelector("img");
            const segmentTween = gsap.to(fish, {
                duration: 1,
                paused: true,
                motionPath: {
                    path: segment,
                    align: segment,
                    alignOrigin: [0.5, 0.5],
                    autoRotate: true,
                },
            });

            segmentTweens.push(segmentTween);

            if (backgroundImage) {
                gsap.fromTo(
                    backgroundImage,
                    { yPercent: 4, scale: 1.08 },
                    {
                        yPercent: -4,
                        ease: "none",
                        scrollTrigger: {
                            trigger: item,
                            start: "top bottom",
                            end: "bottom top",
                            scrub: true,
                            invalidateOnRefresh: true,
                        },
                    },
                );
            }

            ScrollTrigger.create({
                trigger: item,
                start: "top top",
                endTrigger: nextItem || undefined,
                end: nextItem ? "top top" : "bottom top",
                scrub: true,
                markers: showMarkers,
                invalidateOnRefresh: true,
                onEnter: () => {
                    segmentTween.progress(0);
                },
                onLeave: () => {
                    segmentTween.progress(1);
                },
                onEnterBack: () => {
                    segmentTween.progress(1);
                },
                onLeaveBack: () => {
                    segmentTween.progress(0);
                },
                onUpdate: (scrollTrigger) => {
                    if (scrollTrigger.isActive) {
                        segmentTween.progress(scrollTrigger.progress);
                    }
                },
            });

            ScrollTrigger.create({
                trigger: item,
                start: "top " + thresholdFromTop,
                endTrigger: nextItem || undefined,
                end: nextItem ? "top " + thresholdFromTop : "bottom " + thresholdFromTop,
                markers: showMarkers,
                invalidateOnRefresh: true,
                onEnter: () => setActiveBackground(itemIndex),
                onLeave: () => setActiveBackground(itemIndex + 1),
                onEnterBack: () => setActiveBackground(itemIndex),
                onLeaveBack: () => setActiveBackground(Math.max(itemIndex - 1, 0)),
            });
        });

        segmentTweens[0]?.progress(0);
    },
    init: function () {

        sfpReport.initCarousel();
        sfpReport.initScrollSpy();
        sfpReport.initSectionHeaderBackgrounds();
        sfpReport.initTimelineFish();

        sfpReport.initScrollAnimations();

        // Start Alpine after components are registered
        Alpine.start();

    },
};


sfpReport.init();